/**
 * Stop-gap label maps for the ranking breakdown view. Once the app gains
 * a real i18n library, source these from locale files. Kept colocated
 * with the view so adding a new heuristic / guardrail / judge dimension
 * doesn't require hunting across the repo for the label.
 */

export const GUARDRAIL_LABELS: Record<string, { label: string; help: string }> = {
  hook_missing: {
    label: 'Hook ausente',
    help: 'O roteiro não tem hook. Sem hook não há ancoragem nos primeiros segundos.',
  },
  hook_too_long: {
    label: 'Hook longo demais',
    help: 'Hook com mais de 16 palavras. Hooks curtos seguram a atenção; longos se diluem.',
  },
  hook_no_number_or_emphasis: {
    label: 'Hook sem ênfase',
    help: 'Hook não termina em "?" ou "!". Sem ênfase, perde força de abertura.',
  },
  metadata_missing: {
    label: 'Metadados ausentes',
    help: 'O vídeo não tem título, descrição ou hashtags persistidos.',
  },
  title_length_out_of_range: {
    label: 'Título fora da faixa (40–60)',
    help: 'Título precisa ter entre 40 e 60 caracteres pra render no algoritmo.',
  },
  hashtag_count_out_of_range: {
    label: 'Hashtags fora da faixa (3–8)',
    help: 'Quantidade de hashtags precisa estar entre 3 e 8.',
  },
  description_missing_cta: {
    label: 'Descrição sem CTA',
    help: 'A descrição precisa ter um call-to-action (inscreva-se, comente, etc.).',
  },
  thumbnail_missing_highlight_word: {
    label: 'Thumbnail sem highlight',
    help: 'Texto da thumb existe mas não foi escolhida palavra-destaque.',
  },
  audio_duration_out_of_range: {
    label: 'Duração fora da faixa (12–50s)',
    help: 'Áudio narrado precisa durar entre 12 e 50 segundos.',
  },
}

export const HEURISTIC_LABELS: Record<string, { label: string; help: string }> = {
  visual_cuts: {
    label: 'Cortes visuais',
    help: 'Quantidade de cortes detectados via ffprobe sobre a duração — proxy de cadência.',
  },
  novelty: {
    label: 'Novelty semântica',
    help: 'Quão diferente é o roteiro vs. os últimos vídeos publicados (embedding distance).',
  },
  freshness: {
    label: 'Freshness',
    help: 'Recência do conteúdo. Decai com o tempo desde a geração.',
  },
  factory_specific: {
    label: 'Factory-específico',
    help: 'Sub-score que cada factory implementa em factory_specific.py.',
  },
  pattern_score: {
    label: 'Pattern score',
    help: 'Score do padrão narrativo detectado (revelation, count, fact, etc.).',
  },
  pattern_diversity_penalty: {
    label: 'Penalty de diversidade',
    help: 'Aplicado quando o canal repetiu o mesmo padrão demais nos últimos uploads.',
  },
}

export const JUDGE_DIMENSIONS: {
  key: string
  label: string
  help: string
  inverse?: boolean
}[] = [
  {
    key: 'emotional_resonance',
    label: 'Ressonância emocional',
    help: 'Ativação afetiva — o vídeo mexe com o espectador?',
  },
  {
    key: 'surprise_factor',
    label: 'Fator surpresa',
    help: 'O conteúdo entrega algo inesperado em algum ponto?',
  },
  {
    key: 'narrative_tension',
    label: 'Tensão narrativa',
    help: 'Tem arco — pergunta, suspense, revelação?',
  },
  {
    key: 'predictability',
    label: 'Previsibilidade',
    help: 'Quão óbvio é o desfecho. Nota alta = previsível = pior.',
    inverse: true,
  },
  {
    key: 'authentic_voice',
    label: 'Voz autêntica',
    help: 'Soa como pessoa de verdade ou como narração genérica?',
  },
  {
    key: 'factual_risk',
    label: 'Risco factual',
    help: 'Probabilidade de afirmar algo errado. Nota alta = perigoso = pior.',
    inverse: true,
  },
]

export type RankingBreakdownTab = 'group_a' | 'group_b' | 'judge' | 'performance'

export const TAB_LABELS: Record<RankingBreakdownTab, string> = {
  group_a: 'Grupo A',
  group_b: 'Grupo B',
  judge: 'Juiz LLM',
  performance: '× Performance',
}
